<template>
  <div class="page-wrapper">
    <div class="legal-page">
      <h2 class="page-title">Politique de confidentialité</h2>

      <p>Cette politique explique comment nous collectons, utilisons et protégeons vos données personnelles sur
        <strong>dartshub.fr</strong>, service centralisé d'authentification pour la communauté fléchettes.
      </p>

      <h3>1. Données collectées</h3>
      <p>Lors de votre inscription, nous collectons : nom, prénom, email et pseudonyme.</p>
      <p>Lors de votre demande de licence, nous collectons des informations supplémentaires : sexe, nationalité,
        date et lieu de naissance, et adresse complète.</p>
      <p>Lors de vos paiements d'inscription aux événements, des informations de paiement sont traitées via
        HelloAsso, nous ne stockons pas les détails de votre carte bancaire.</p>

      <p>Lors d'une demande d'affiliation de club, nous collectons : les coordonnées du club et de son
        contact, la composition du bureau (nom, prénom, téléphone et email de chaque membre) et les pièces
        justificatives (statuts, récépissé de déclaration en préfecture, RIB du club).</p>

      <h3>2. Finalités du traitement</h3>
      <ul>
        <li>Gestion des comptes utilisateurs et authentification centralisée</li>
        <li>Paiement des inscriptions aux événements et tournois</li>
        <li>Affichage personnalisé des classements</li>
        <li>Gestion des demandes de licence et transmission à la Fédération Française de Darts</li>
        <li>Gestion des demandes d'affiliation des clubs</li>
        <li>Prévention de la fraude et gestion des litiges</li>
      </ul>
      <p>La base légale du traitement est l'exécution du contrat (paiements, demandes de licence), votre
        consentement (cookies non essentiels), et nos intérêts légitimes (sécurité et amélioration du service).</p>

      <h3>3. Destinataires</h3>
      <p>DartsHub est un service central d'authentification. Selon le
        site auquel vous accédez avec votre compte DartsHub, vos données peuvent être partagées avec les sites
        partenaires concernés (Badarts, CMER, LSEF et autres comités affiliés), chacun dans la limite de son
        propre usage (licences, inscriptions, classements). Elles peuvent également être partagées avec HelloAsso
        pour le traitement des paiements (voir la <a href="https://www.helloasso.com/politique-de-confidentialite"
          class="legal-link" target="_blank">politique de confidentialité de HelloAsso</a>), et la Fédération
        Française de Darts pour la gestion des licences (voir les <a href="https://www.ffdarts.fr/mentions-legales/"
          class="legal-link" target="_blank">mentions légales de la FFD</a>).</p>

      <p>Les demandes d'affiliation et leurs pièces justificatives ne sont accessibles qu'au comité auquel le
        club demande son affiliation, ainsi qu'aux administrateurs de la plateforme DartsHub.</p>

      <h3>4. Durée de conservation</h3>
      <p>Les données sont conservées pendant la durée de votre inscription, puis supprimées dans un délai d'un an
        après désinscription. Les données relatives aux licences sont conservées le temps nécessaire à leur
        transmission à la Fédération Française de Darts.</p>

      <p>Les pièces justificatives d'affiliation sont conservées tant que le club est affilié, puis supprimées
        dans un délai d'un an après la fin de sa dernière saison d'affiliation.</p>

      <h3>5. Transferts de données hors UE</h3>
      <p>Certaines données peuvent être transférées vers des pays hors Union Européenne via nos prestataires.
        Nous nous assurons que ces transferts sont protégés par des clauses contractuelles types conformes au RGPD.</p>

      <h3>6. Mesures de sécurité</h3>
      <p>Nous mettons en œuvre des mesures techniques et organisationnelles pour protéger vos données : chiffrement
        des transmissions (HTTPS), authentification sécurisée (JWT), et sauvegardes régulières. Les pièces
      justificatives d'affiliation sont stockées hors de tout accès public et ne sont téléchargeables que par le
      comité concerné.</p>

      <h3>7. Vos droits</h3>
      <p>Conformément au RGPD, vous disposez d'un droit d'accès, de rectification, de suppression, de limitation
        du traitement, de portabilité et d'opposition à vos données. Vous pouvez retirer votre consentement à
        tout moment.</p>
      <p>Vous pouvez exercer ces droits en nous écrivant via le
        <RouterLink to="/contact" class="legal-link">formulaire de contact</RouterLink>,
        en fournissant une preuve d'identité si nécessaire.
      </p>
      <p>Vous avez également le droit d'introduire une réclamation auprès de la CNIL via
        <a href="https://cnil.fr" class="legal-link" target="_blank">www.cnil.fr</a>.
      </p>

      <h3>8. Cookies</h3>
      <p>Nous utilisons des cookies nécessaires au bon fonctionnement du site. Nous n'utilisons pas de cookies
        de mesure d'audience. Vous devez accepter les cookies pour accéder aux zones nécessitant une authentification.
      </p>

      <h3>9. Traitement des données des mineurs</h3>
      <p>Le site est accessible aux juniors (moins de 18 ans). Pour les demandes de licence impliquant des mineurs,
        une autorisation parentale est requise. Les parents peuvent exercer les droits RGPD au nom de l'enfant.</p>

      <p><em>Dernière mise à jour : 26 octobre 2026</em></p>
    </div>

    <Footer backLink />
  </div>
</template>

<script setup lang="ts">
import Footer from '@/components/Footer.vue';
</script>

<style scoped>
.page-wrapper {
  min-height: 100svh;
  display: flex;
  flex-direction: column;
}

.legal-page {
  flex: 1;
  max-width: 800px;
  margin: 2rem auto;
  padding: 2rem;
  background: var(--bg-card);
  border-radius: var(--radius);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
}

h2 {
  margin-bottom: 1.5rem;
}

h3 {
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
}

p,
li {
  line-height: 1.7;
}

ul {
  padding-left: 1.5rem;
  margin-bottom: 1rem;
}

.legal-link {
  color: var(--primary);
  font-weight: 600;
  text-decoration: none;
}

.legal-link:hover {
  text-decoration: underline;
}

</style>
